import { SESSION_USER_STORAGE_KEY } from '@/lib/api/request-scope'
import { browserLoginScope } from '@/lib/auth/browser-login-scope'
import {
  desktopAccountSession,
  loadNativeCredentialStore,
} from '@/lib/utils/native-credential-store'
import { isTauri } from '@/lib/utils/tauri'

import { createSessionScope } from '@scrambl/shared/session-scope'

import {
  browserOAuthProof,
  createOAuthProofOwner,
  OAUTH_PROOF_KEY,
} from './oauth-proof'

// A full provider redirect reloads the JS profile store. Use the browser's
// existing cross-tab identity announcement so hydration does not cancel its receipt.
const browserSession = createSessionScope()
function captureBrowserSession() {
  const login = browserLoginScope.capture()
  const userId = localStorage.getItem(SESSION_USER_STORAGE_KEY) || null
  browserSession.transition(JSON.stringify([userId, login.identity]))
  const session = browserSession.capture()
  return {
    ...session,
    identity: userId,
    assertCurrent() {
      login.assertCurrent()
      // Also fence a changed user announcement if its storage event was missed.
      browserSession.transition(
        JSON.stringify([
          localStorage.getItem(SESSION_USER_STORAGE_KEY) || null,
          browserLoginScope.capture().identity,
        ]),
      )
      session.assertCurrent()
    },
  }
}
if (typeof window !== 'undefined')
  window.addEventListener('storage', event => {
    if (event.key === null || event.key === SESSION_USER_STORAGE_KEY)
      captureBrowserSession()
  })

export const oauthProof = createOAuthProofOwner({
  async read() {
    if (isTauri()) {
      const store = await loadNativeCredentialStore()
      return store.get(OAUTH_PROOF_KEY)
    }
    const raw = sessionStorage.getItem(OAUTH_PROOF_KEY)
    if (!raw) return null
    try {
      return JSON.parse(raw) as unknown
    } catch {
      return null
    }
  },
  async write(value, expectedId) {
    if (isTauri()) {
      const store = await loadNativeCredentialStore()
      if (
        value &&
        value.ownerKey !== desktopAccountSession.capture().credentialSessionKey
      )
        throw new Error('The active login changed. Start signing in again.')
      if (
        expectedId &&
        (await store.get<{ id?: string }>(OAUTH_PROOF_KEY))?.id !== expectedId
      )
        throw new Error('This sign-in attempt is no longer active.')
      if (value) await store.set(OAUTH_PROOF_KEY, value)
      else await store.delete(OAUTH_PROOF_KEY)
      await store.save()
    } else {
      if (expectedId) {
        const raw = sessionStorage.getItem(OAUTH_PROOF_KEY)
        if (!raw || (JSON.parse(raw) as { id?: string }).id !== expectedId)
          throw new Error('This sign-in attempt is no longer active.')
      }
      if (value) sessionStorage.setItem(OAUTH_PROOF_KEY, JSON.stringify(value))
      else sessionStorage.removeItem(OAUTH_PROOF_KEY)
    }
  },
  createProof: browserOAuthProof,
  randomUUID: () => crypto.randomUUID(),
  capture: () =>
    isTauri() ? desktopAccountSession.capture() : captureBrowserSession(),
  persistentOwner: () =>
    isTauri()
      ? desktopAccountSession.capture().credentialSessionKey
      : browserLoginScope.capture().identity,
})
